/* @flow */
// typecheck: ignore file
import cx from 'classnames';
import React from 'react';
import shallowCompare from 'react-addons-shallow-compare';
import { debounce, range } from 'lodash';

import Chat from 'entities/Chat';
import ChatMessage, { ChatGapMessage } from 'entities/ChatMessage';
import { GapMessage } from 'entities/Message';
import Group from 'entities/Group';
import LayerTrigger from 'components/LayerTrigger.jsx';
import Tooltip from 'components/Tooltip.jsx';
import AnnouncementMessageListItem from './AnnouncementMessageListItem.jsx';
import MessageListWelcome from './MessageListWelcome.jsx';
import MessageListItem from './MessageListItem.jsx';
import MessageListSystemItem from './MessageListSystemItem.jsx';
import MessageListTimeItem from './MessageListTimeItem.jsx';
import ScrollableArea from 'components/ScrollableArea.jsx';

import GapMessageItem from './GapMessageItem.jsx';

import { extractListItems } from 'chat/lib/MessageHistory';

import type { MessageBunch } from 'chat/lib/MessageHistory';
import type { MessageType } from 'chat/lib/MessageInterfacePropType';

import './styles/MessageList.scss';

function allChatMessages(messages: MessageType[]) {
  const chatMessages: ChatMessage[] = [];
  for (const m of messages) {
    if (m instanceof ChatMessage) {
      chatMessages.push(m);
    } else {
      console.error('unexpected not ChatMessage', m);
    }
  }
  return chatMessages;
}

type Props = {
  chat?: Chat,
  group?: Group,
  unreadCount?: number,
  messages: MessageType[],
  onScrollToBottom?: Function,
  loadMoreMessages: Function,
  subgroupRole?: string,
  className?: string,
};

type State = {
  tooltip: boolean,
  items: *[],
};

class MessageList extends React.Component {
  props: Props;
  state: State;

  constructor(props: Props) {
    super(props);
    this.state = {
      tooltip: false,
      items: extractListItems(props.messages),
    };
  }

  componentWillReceiveProps(nextProps: Props) {
    if (nextProps.messages !== this.props.messages) {
      this.setState({
        items: extractListItems(nextProps.messages),
      });
    }
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    return shallowCompare(this, nextProps, nextState);
  }

  // TODO refactor this to avoid setState
  componentDidUpdate(prevProps: Props) {
    const unreads = this.props.unreadCount || 0;

    if (unreads > 0 && !this.isScrolledToBottom()
      && !this.state.tooltip) {
      this.refs.trigger.showLayer();
      this.setState({ tooltip: true }); // eslint-disable-line react/no-did-update-set-state
    }

    if (this.props.chat !== prevProps.chat) {
      this.scrollToBottom();
    }

    if (prevProps.messages.length === this.props.messages.length - 1) {
      this.onReceiveNewMessage();
    }
  }

  // debounce (not throttle) because we only need to carry out this logic if
  // the user is at the bottom once their scrolling settles
  detectScrolledToBottom = debounce(() => {
    if (this.isScrolledToBottom()) {
      if (this.props.onScrollToBottom) {
        this.props.onScrollToBottom();
      }
      this.setState({ tooltip: false });
      this.refs.trigger.handleDelayedHide();
    }
  }, 200);

  isScrolledToBottom = () => {
    return this.refs.scrollable && this.refs.scrollable.isScrolledToBottom(50);
  };

  scrollToBottom = () => {
    if (this.refs.scrollable) {
      this.refs.scrollable.scrollToBottom();
      this.detectScrolledToBottom();
    }
  };

  onReceiveNewMessage() {
    if (this.isScrolledToBottom()) {
      this.scrollToBottom();
    }
  }

  renderBunch(bunch: MessageBunch) {
    const { messages } = bunch;
    const { chat, group } = this.props;
    const isUnsubscribed = chat ? chat.isUnsubscribed() : false;
    const isGroupChat = chat ? chat.isGroupChat() : false;
    const itemKey = messages[0].getUuid();

    switch (bunch.messageType) {
      case 'broadcast':
      case 'message':
        return (
          <AnnouncementMessageListItem
            key={itemKey}
            group={group}
            messages={messages}
            isGroupChat={isGroupChat}
          />
        );
      case 'attachment':
      case 'text':
        return (
          <MessageListItem
            key={itemKey}
            messages={allChatMessages(messages)}
            isGroupChat={isGroupChat}
            disabled={isUnsubscribed}
          />
        );
      case 'system':
        return (
          <MessageListSystemItem
            key={itemKey}
            messages={messages}
          />
        );
      case 'gap':
        console.error('renderBunch received gap message');
        return undefined;
      default:
        console.error('Unknown Message Type: ' + bunch.messageType);
        return undefined;
    }
  }

  _hasErroredOnItemType: boolean;

  _itemRenderer = ({ index }: {index: number}) => {
    const item = this.state.items[index];

    if (item.messageType) {
      // $FlowFixMe not sure how to cast this
      const bunch: MessageBunch = item;
      return this.renderBunch(bunch);
    } else if (item instanceof ChatGapMessage || item instanceof GapMessage) {
      return (
        <GapMessageItem
          key={item.getUuid()}
          message={item}
          onVisible={this.props.loadMoreMessages}
        />
      );
    } else if (item instanceof Date) {
      return <MessageListTimeItem key={item.toString()} date={item} />;
    } else {
      if (!this._hasErroredOnItemType) {
        console.warn('Unknown item type at index', index, item);
        console.error('DEBUG MessageList returning null for item');
        this._hasErroredOnItemType = true;
      }
      return null;
    }
  };

  renderMessages() {
    const { items } = this.state;

    const result = range(items.length).map(index => this._itemRenderer({ index }));

    return <ul className="MessageList-messages">{result}</ul>;
  }

  renderTooltipLauncher() {
    const unreads = this.props.unreadCount || 0;

    const messageText = unreads === 1 ?
      'You have 1 unread message' :
      'You have ' + unreads + ' unread messages';

    const tooltip = (
      <Tooltip
        onClick={this.scrollToBottom}
        className={cx({
          'MessageList-tooltip': true,
          'MessageList-unread': this.state.tooltip,
          'MessageList-unreadFade': !this.state.tooltip,
        })}
        hideOnEscape={false}
        hideOnBlur={false}>
        {messageText}
      </Tooltip>
    );

    return (
      <LayerTrigger
        ref="trigger"
        layer={tooltip}
        trigger="manual"
        placement="top"
        alignment="middle">
        <div />
      </LayerTrigger>
    );
  }

  renderWelcome() {
    const { chat, group, subgroupRole } = this.props;

    return <MessageListWelcome chat={chat} group={group} subgroupRole={subgroupRole} />;
  }

  render() {
    return (
      <div
        className={cx('MessageList', this.props.className)}>
        <ScrollableArea
          useBodyChildrenHeight
          scrollToBottom
          ref="scrollable"
          onScroll={this.detectScrolledToBottom}>
          {this.renderWelcome()}
          {this.renderMessages()}
        </ScrollableArea>
        {this.renderTooltipLauncher()}
      </div>
    );
  }
}

export default MessageList;



// WEBPACK FOOTER //
// ./src/chat/components/MessageList.jsx